import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Reveal, SectionHeading } from "./Reveal";
import { Field, SectionTitle, inputCls } from "./booking/fields";

const EMPTY = {
  name: "", phone: "", email: "",
  dogName: "", dogBreed: "", dogAge: "", dogWeight: "", healthConcerns: "", healthDetails: "",
  service: "", preferredDate: "", notes: "",
};

export default function BookingForm({ services, settings }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const reset = () => { setForm(EMPTY); setDone(false); setError(""); };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.healthConcerns) { setError("Please tell us whether your dog has any health concerns."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/bookings", form);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <SectionHeading
          kicker="Request Online"
          title="Request an appointment."
          description="Tell us about you and your pup and we'll call or email you back to confirm a spot, usually the same day."
        />
        <Reveal delay={0.1} className="mt-12">
          {done ? (
            <div data-testid="booking-success" role="status" className="rounded-lg border border-grass-500/25 bg-grass-100/60 p-8 text-left sm:p-10">
              <p className="text-2xl font-bold text-stone-900">Request received!</p>
              <p className="mt-2 max-w-md text-sm text-stone-600">
                Thanks {form.name.split(" ")[0]}, we'll reach out shortly to confirm {form.dogName || "your pup"}'s appointment.
                Need us sooner? Call {settings.phone}.
              </p>
              <p data-testid="booking-success-waiver-note" className="mt-4 max-w-md rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Before your visit, please sign our <Link to="/waiver" className="font-semibold underline underline-offset-2">grooming waiver</Link> and upload {form.dogName || "your dog"}'s rabies vaccination record. Proof of current rabies vaccination is required on the day of the appointment.
              </p>
              <button onClick={reset} data-testid="booking-another-button" className="btn-secondary mt-6 px-6 py-2.5 text-sm font-semibold">
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="booking-form" className="space-y-10 rounded-lg border border-lavender-700/10 bg-lavender-50 p-7 shadow-soft sm:p-10">
              <div data-testid="booking-customer-section">
                <SectionTitle step="01" title="Customer information" hint="How we reach you to confirm." />
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <Field id="bk-name" label="Your name *" className="sm:col-span-2">
                    <input id="bk-name" required value={form.name} onChange={set("name")} data-testid="booking-name-input" className={`${inputCls} mt-1.5`} placeholder="Jane Doe" autoComplete="name" />
                  </Field>
                  <Field id="bk-phone" label="Phone *">
                    <input id="bk-phone" type="tel" required value={form.phone} onChange={set("phone")} data-testid="booking-phone-input" className={`${inputCls} mt-1.5`} placeholder="860-555-0123" autoComplete="tel" />
                  </Field>
                  <Field id="bk-email" label="Email *">
                    <input id="bk-email" type="email" required value={form.email} onChange={set("email")} data-testid="booking-email-input" className={`${inputCls} mt-1.5`} placeholder="jane@email.com" autoComplete="email" />
                  </Field>
                </div>
              </div>

              <div data-testid="booking-dog-section">
                <SectionTitle step="02" title="Dog information" hint="Helps us plan the right time and care for your pup." />
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <Field id="bk-dog" label="Dog's name *">
                    <input id="bk-dog" required value={form.dogName} onChange={set("dogName")} data-testid="booking-dog-name-input" className={`${inputCls} mt-1.5`} placeholder="Bella" />
                  </Field>
                  <Field id="bk-breed" label="Breed *">
                    <input id="bk-breed" required value={form.dogBreed} onChange={set("dogBreed")} data-testid="booking-breed-input" className={`${inputCls} mt-1.5`} placeholder="Mini Goldendoodle" />
                  </Field>
                  <Field id="bk-age" label="Age *">
                    <input id="bk-age" required value={form.dogAge} onChange={set("dogAge")} data-testid="booking-age-input" className={`${inputCls} mt-1.5`} placeholder="3 years" />
                  </Field>
                  <Field id="bk-weight" label="Weight *">
                    <input id="bk-weight" required value={form.dogWeight} onChange={set("dogWeight")} data-testid="booking-weight-input" className={`${inputCls} mt-1.5`} placeholder="25 lbs" />
                  </Field>
                  <fieldset className="sm:col-span-2" data-testid="booking-health-fieldset">
                    <legend className="text-sm font-medium text-stone-600">Any health concerns? *</legend>
                    <div className="mt-2 flex gap-3">
                      {["no", "yes"].map((v) => (
                        <label key={v} className={`flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors ${form.healthConcerns === v ? "border-lavender-700 bg-lavender-700 text-white" : "border-lavender-700/15 bg-white text-stone-700 hover:border-lavender-400"}`}>
                          <input type="radio" name="healthConcerns" value={v} required checked={form.healthConcerns === v} onChange={() => patch({ healthConcerns: v, healthDetails: v === "no" ? "" : form.healthDetails })} data-testid={`booking-health-${v}`} className="sr-only" />
                          {v === "no" ? "No" : "Yes"}
                        </label>
                      ))}
                    </div>
                    {form.healthConcerns === "yes" && (
                      <div className="mt-4">
                        <label className="text-sm font-medium text-stone-600" htmlFor="bk-health">Please explain *</label>
                        <textarea id="bk-health" rows={3} required value={form.healthDetails} onChange={set("healthDetails")} data-testid="booking-health-details-input" className={`${inputCls} mt-1.5 resize-none`} placeholder="Allergies, skin conditions, joint issues, medications, anxiety…" />
                      </div>
                    )}
                  </fieldset>
                  <Field id="bk-service" label="Service *">
                    <select id="bk-service" required value={form.service} onChange={set("service")} data-testid="booking-service-select" className={`${inputCls} mt-1.5`}>
                      <option value="">Choose a service…</option>
                      {services.map((s) => (
                        <option key={s.id || s.name} value={s.name}>{s.name}</option>
                      ))}
                      <option value="Not sure yet">Not sure yet</option>
                    </select>
                  </Field>
                  <Field id="bk-date" label="Preferred day *">
                    <input id="bk-date" required value={form.preferredDate} onChange={set("preferredDate")} data-testid="booking-date-input" className={`${inputCls} mt-1.5`} placeholder="e.g. Any Saturday morning" />
                  </Field>
                  <Field id="bk-notes" label="Anything else we should know? (optional)" className="sm:col-span-2">
                    <textarea id="bk-notes" rows={2} value={form.notes} onChange={set("notes")} data-testid="booking-notes-input" className={`${inputCls} mt-1.5 resize-none`} placeholder="Nervous around dryers, sensitive paws, first visit…" />
                  </Field>
                </div>
              </div>

              {error && <p data-testid="booking-error" className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-stone-500">
                  * Required. New here? Please also sign our <Link to="/waiver" data-testid="booking-waiver-link" className="font-semibold text-lavender-700 underline underline-offset-2">grooming waiver</Link> before your first visit.
                </p>
                <button type="submit" disabled={busy} data-testid="booking-submit-button" className="btn-primary inline-flex items-center gap-2.5 px-8 py-3.5 text-sm font-semibold disabled:opacity-60">
                  <span>{busy ? "Sending request…" : "Send request"}</span>
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
